<script lang="ts" setup>
import { ref, computed, watch, toRef } from "vue";
import FormBuilderEditor from "@/components/forms/FormBuilderEditor.vue";
import FormPreview from "@/components/forms/FormPreview.vue";
import type { VueFormConfig } from "@/types/wizard-types";
import type { WizardDefinition } from "@/types/workflow-types";
import { useSaveWizardDefinition } from "@/queries/workflows";
import {
  type BuilderWizardStep,
  toBuilderSteps,
  toSavePayload,
} from "@/utils/wizard-mappers";

type WizardStep = BuilderWizardStep;

interface CustomStepTemplate {
  id: string;
  name: string;
  componentName: string;
  description: string;
  icon: string;
}

interface Props {
  workflowId: string;
  workflowName: string;
  show: boolean;
  wizardDefinitions?: WizardDefinition[];
}

interface Emits {
  (e: "close"): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const { saveWizardDefinitionAsync, saveWizardDefinitionPending } =
  useSaveWizardDefinition(toRef(props, "workflowId"));

const steps = ref<WizardStep[]>([]);
const selectedStepId = ref<string>("");
const showCustomTemplates = ref(false);
const showDropdown = ref(false);
const deleteStepId = ref<string | null>(null);
let stepCounter = 1;

// Load existing wizard definitions when the modal opens
watch(
  () => props.show,
  (visible) => {
    if (visible && props.wizardDefinitions?.length) {
      const loaded = toBuilderSteps(props.wizardDefinitions);
      steps.value = loaded;
      stepCounter = loaded.length + 1;
      selectedStepId.value = loaded[0]?.id ?? "";
    }
  },
  { immediate: true }
);

// Pre-built custom step templates
const customStepTemplates: CustomStepTemplate[] = [
  {
    id: "data-mapping",
    name: "Data Mapping Tool",
    componentName: "DataMappingTool",
    description: "Interactive tool for mapping data fields between systems",
    icon: "bi-shuffle",
  },
  {
    id: "approval-flow",
    name: "Approval Workflow",
    componentName: "ApprovalWorkflow",
    description: "Multi-level approval process designer",
    icon: "bi-diagram-3",
  },
  {
    id: "template-editor",
    name: "Template Editor",
    componentName: "TemplateEditor",
    description: "Rich editor for email/document templates",
    icon: "bi-file-earmark-text",
  },
  {
    id: "schedule-builder",
    name: "Schedule Builder",
    componentName: "ScheduleBuilder",
    description: "Visual cron-like schedule configuration",
    icon: "bi-calendar-event",
  },
];

const orderedSteps = computed(() => {
  return [...steps.value].sort((a, b) => a.order - b.order);
});

const selectedStep = computed(() => {
  return steps.value.find((s) => s.id === selectedStepId.value);
});

const addStep = () => {
  const newStep: WizardStep = {
    id: `step_${Date.now()}`,
    title: `Step ${stepCounter++}`,
    order: steps.value.length + 1,
    type: "vueform",
    vueformConfig: {
      schema: {},
      displayErrors: true,
    },
  };

  steps.value.push(newStep);
  selectedStepId.value = newStep.id;
  showDropdown.value = false;
};

const onFormConfigUpdate = (config: VueFormConfig) => {
  if (selectedStep.value) {
    selectedStep.value.vueformConfig = config;
    // Trigger auto-save when form config changes
    autoSaveWizard();
  }
};

const addCustomStep = (template: CustomStepTemplate) => {
  const newStep: WizardStep = {
    id: `step_${Date.now()}`,
    title: template.name,
    order: steps.value.length + 1,
    type: "custom",
    customTemplate: {
      name: template.name,
      componentName: template.componentName,
      description: template.description,
      icon: template.icon,
    },
  };

  steps.value.push(newStep);
  selectedStepId.value = newStep.id;
  showCustomTemplates.value = false;
  showDropdown.value = false;
};

const removeStep = (stepId: string) => {
  const index = steps.value.findIndex((s) => s.id === stepId);
  if (index > -1) {
    steps.value.splice(index, 1);
    // Reorder
    steps.value.forEach((step, idx) => {
      step.order = idx + 1;
    });

    if (selectedStepId.value === stepId) {
      selectedStepId.value = "";
    }
  }
};

// --- Drag & Drop for steps list ---
const draggedStepIndex = ref<number | null>(null);

const onStepDragStart = (e: DragEvent, index: number) => {
  draggedStepIndex.value = index;
  e.dataTransfer?.setData('text/plain', String(index));
  e.dataTransfer!.effectAllowed = 'move';
};

const onStepDragOver = (e: DragEvent) => {
  e.preventDefault();
  e.dataTransfer!.dropEffect = 'move';
};

const onStepDrop = (e: DragEvent, dropIndex: number) => {
  e.preventDefault();
  const fromIndex = draggedStepIndex.value;
  if (fromIndex === null || fromIndex === dropIndex) {
    draggedStepIndex.value = null;
    return;
  }

  const [moved] = steps.value.splice(fromIndex, 1);
  steps.value.splice(dropIndex, 0, moved);

  // Reorder
  steps.value.forEach((step, idx) => {
    step.order = idx + 1;
  });

  draggedStepIndex.value = null;
};

const onStepDragEnd = () => {
  draggedStepIndex.value = null;
};

const updateStepTitle = (value: string) => {
  if (selectedStep.value) {
    selectedStep.value.title = value;
  }
};

const toggleCustomTemplates = () => {
  showCustomTemplates.value = !showCustomTemplates.value;
  showDropdown.value = false;
};

// Save wizard configuration
const savedIndicator = ref(false);
let saveDebounceTimer: ReturnType<typeof setTimeout> | null = null;

async function persistWizard() {
  const payload = toSavePayload(props.workflowName, steps.value);
  await saveWizardDefinitionAsync(payload);
}

const autoSaveWizard = () => {
  if (saveDebounceTimer) clearTimeout(saveDebounceTimer);
  saveDebounceTimer = setTimeout(() => persistWizard(), 2000);
};

const saveWizard = async () => {
  await persistWizard();
  savedIndicator.value = true;
  setTimeout(() => (savedIndicator.value = false), 2000);
};
</script>

<template>
  <div
    v-if="show"
    class="modal fade show d-block"
    tabindex="-1"
    @click="emit('close')"
  >
    <div class="modal-dialog modal-xl" @click.stop>
      <div class="modal-content">
        <!-- Modal Header -->
        <div class="modal-header">
          <div>
            <h2 class="modal-title mb-0">Wizard Builder</h2>
            <small class="text-muted">{{ workflowName }}</small>
          </div>
          <button
            type="button"
            class="btn-close"
            @click="emit('close')"
          ></button>
        </div>

        <!-- Modal Body -->
        <div class="modal-body p-0">
          <div class="row g-0" style="height: 70vh">
            <!-- Left Panel: Steps List -->
            <div class="col-3 border-end d-flex flex-column">
              <div class="p-3 border-bottom bg-body-tertiary flex-shrink-0">
                <div class="d-flex justify-content-between align-items-center">
                  <h3 class="mb-0">Wizard Steps</h3>
                  <div class="dropdown">
                    <button
                      class="bb-btn-primary-cta btn-sm dropdown-toggle"
                      type="button"
                      @click="showDropdown = !showDropdown"
                      :aria-expanded="showDropdown"
                    >
                      <i class="bi bi-plus"></i> Add Step
                    </button>
                    <ul class="dropdown-menu" :class="{ show: showDropdown }">
                      <li>
                        <a
                          class="dropdown-item"
                          href="#"
                          @click.prevent="addStep"
                        >
                          <i class="bi bi-ui-checks me-2"></i>
                          Form Builder
                          <small class="text-muted d-block"
                            >Create custom forms to collect user data</small
                          >
                        </a>
                      </li>
                      <li><hr class="dropdown-divider" /></li>
                      <li>
                        <a
                          class="dropdown-item"
                          href="#"
                          @click.prevent="toggleCustomTemplates"
                        >
                          <i class="bi bi-puzzle me-2"></i>
                          Custom Template
                          <small class="text-muted d-block"
                            >Pre-built interactive tools</small
                          >
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div class="flex-grow-1 overflow-auto p-2 steps-list-container">
                <div
                  v-if="orderedSteps.length === 0"
                  class="text-center text-muted py-5"
                >
                  <i class="bi bi-magic display-6"></i>
                  <p class="mt-2">No steps yet</p>
                </div>

                <div
                  v-for="(step, index) in orderedSteps"
                  :key="step.id"
                  class="card mb-2 user-select-none step-card"
                  :class="{
                    'border-primary': selectedStepId === step.id,
                    'step-dragging': draggedStepIndex === index,
                  }"
                  draggable="true"
                  @dragstart="onStepDragStart($event, index)"
                  @dragend="onStepDragEnd"
                  @dragover="onStepDragOver"
                  @drop="onStepDrop($event, index)"
                  @click="selectedStepId = step.id"
                  style="cursor: pointer"
                >
                  <div class="card-body p-2">
                    <div class="d-flex align-items-center gap-2">
                      <!-- Drag handle -->
                      <div class="step-drag-handle text-muted">
                        <i class="bi bi-grip-vertical"></i>
                      </div>
                      <span class="badge bg-primary rounded-pill">{{
                        index + 1
                      }}</span>
                      <div class="flex-grow-1 min-w-0">
                        <div class="fw-medium text-truncate">{{ step.title }}</div>
                        <small
                          class="text-muted d-flex align-items-center gap-1"
                        >
                          <i
                            :class="
                              step.type === 'vueform'
                                ? 'bi-ui-checks'
                                : step.customTemplate?.icon || 'bi-puzzle'
                            "
                          ></i>
                          {{
                            step.type === "vueform"
                              ? "Form"
                              : step.customTemplate?.name || "Custom"
                          }}
                        </small>
                      </div>
                      <button
                        @click.stop="deleteStepId = step.id"
                        class="bb-btn-danger btn-sm"
                      >
                        <i class="bi bi-trash me-1"></i>Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Panel: Step Editor -->
            <div class="col-9 d-flex flex-column">
              <div
                v-if="!selectedStep"
                class="d-flex flex-column justify-content-center align-items-center h-100 text-muted"
              >
                <i class="bi bi-arrow-left display-6"></i>
                <p class="mt-2">Select a step to edit</p>
              </div>

              <div v-else class="p-4 flex-grow-1 overflow-auto step-editor-content">
                <div class="mb-4">
                  <label class="form-label">Step Title</label>
                  <input
                    type="text"
                    class="form-control"
                    :value="selectedStep.title"
                    @input="
                      updateStepTitle(($event.target as HTMLInputElement).value)
                    "
                  />
                  <small class="text-muted">
                    Per-field defaults and upgrade policy are configured on each
                    field below (Edit field → Default Value + On Publish).
                  </small>
                </div>

                <hr />

                <!-- Form Configuration -->
                <div v-if="selectedStep.type === 'vueform'" class="form-builder-container">
                  <div class="row g-3">
                    <div class="col-md-5 d-flex flex-column">
                      <FormBuilderEditor
                        :key="selectedStepId"
                        :model-value="selectedStep.vueformConfig"
                        @update:model-value="onFormConfigUpdate"
                      />
                    </div>
                    <div class="col-md-7 d-flex flex-column">
                      <FormPreview
                        :schema="selectedStep.vueformConfig?.schema"
                      />
                    </div>
                  </div>
                </div>

                <!-- Custom Template Configuration -->
                <div v-else-if="selectedStep.type === 'custom'">
                  <h3>Custom Template Configuration</h3>
                  <p class="text-muted small">
                    {{ selectedStep.customTemplate?.description }}
                  </p>

                  <div class="card mb-3">
                    <div class="card-body">
                      <div class="d-flex align-items-center gap-3">
                        <div class="bg-light-subtle rounded p-2">
                          <i
                            :class="
                              selectedStep.customTemplate?.icon || 'bi-puzzle'
                            "
                            class="text-primary"
                            style="font-size: 1.5rem"
                          ></i>
                        </div>
                        <div>
                          <p class="fw-medium mb-1">
                            {{ selectedStep.customTemplate?.name }}
                          </p>
                          <small class="text-muted"
                            >Component:
                            {{
                              selectedStep.customTemplate?.componentName
                            }}</small
                          >
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="alert alert-warning">
                    <i class="bi bi-puzzle me-2"></i>
                    Custom template configuration will be integrated here based
                    on the selected component type
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer">
          <button
            type="button"
            class="bb-btn-cancel"
            @click="emit('close')"
          >
            <i class="bi bi-x-circle me-1"></i> Close
          </button>
          <button
            type="button"
            class="bb-btn-primary-cta"
            :disabled="saveWizardDefinitionPending"
            @click="saveWizard"
          >
            <i
              :class="
                savedIndicator
                  ? 'bi bi-check-circle me-1'
                  : saveWizardDefinitionPending
                  ? 'bi bi-arrow-repeat spin me-1'
                  : 'bi bi-save me-1'
              "
            ></i>
            {{ savedIndicator ? "Saved" : saveWizardDefinitionPending ? "Saving..." : "Save Wizard" }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Delete Step Confirmation Modal -->
  <div
    v-if="deleteStepId"
    class="modal fade show d-block"
    tabindex="-1"
    @click.self="deleteStepId = null"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title text-danger">
            <i class="bi bi-exclamation-triangle me-2"></i>Delete Step
          </h2>
          <button type="button" class="btn-close" @click="deleteStepId = null"></button>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to delete step <strong>{{ steps.find(s => s.id === deleteStepId)?.title }}</strong>?</p>
          <p class="text-muted mb-0">This action cannot be undone.</p>
        </div>
        <div class="modal-footer">
          <button class="bb-btn-cancel" @click="deleteStepId = null">Cancel</button>
          <button class="btn btn-danger" @click="() => { removeStep(deleteStepId!); deleteStepId = null; }">Delete</button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="deleteStepId" class="modal-backdrop fade show" @click="deleteStepId = null"></div>

  <!-- Custom Template Selection Modal -->
  <div
    v-if="showCustomTemplates"
    class="modal fade show d-block"
    tabindex="-1"
    @click="showCustomTemplates = false"
  >
    <div class="modal-dialog modal-lg" @click.stop>
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title">Select Custom Template</h2>
          <button
            type="button"
            class="btn-close"
            @click="showCustomTemplates = false"
          ></button>
        </div>
        <div class="modal-body">
          <p class="text-muted mb-4">
            Choose a pre-built interactive tool for your workflow step:
          </p>
          <div class="row g-3">
            <div
              v-for="template in customStepTemplates"
              :key="template.id"
              class="col-md-6"
            >
              <div
                class="card h-100 cursor-pointer border-2 hover-card"
                @click="addCustomStep(template)"
              >
                <div class="card-body d-flex align-items-center gap-3">
                  <div class="bg-light-subtle rounded p-3">
                    <i
                      :class="template.icon"
                      class="text-primary"
                      style="font-size: 1.5rem"
                    ></i>
                  </div>
                  <div class="flex-grow-1">
                    <p class="fw-medium mb-1">{{ template.name }}</p>
                    <small class="text-muted">{{ template.description }}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="bb-btn-cancel"
            @click="showCustomTemplates = false"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.modal {
  background-color: rgba(0, 0, 0, 0.5);
}

.cursor-pointer {
  cursor: pointer;
}

.modal-content .hover-card:hover {
  border-color: var(--bs-primary);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
  transition: all 0.2s ease;
}

/* Ensure steps list is properly scrollable */
.steps-list-container {
  min-height: 0; /* Important for flex container scrolling */
  max-height: calc(70vh - 80px); /* Account for header height */
}

.step-card {
  transition: opacity 0.2s, box-shadow 0.2s;
}

.step-drag-handle {
  cursor: grab;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.step-drag-handle:active {
  cursor: grabbing;
}

.step-dragging {
  opacity: 0.4;
}

/* Ensure step editor content is scrollable and doesn't overflow */
.step-editor-content {
  min-height: 0; /* Important for flex container scrolling */
  max-height: 100%;
  overflow-y: auto;
}

/* Form builder container with constrained height */
.form-builder-container {
  display: flex;
  flex-direction: column;
  height: calc(70vh - 250px);
}

.form-builder-container .row {
  height: 100%;
}

/* Fix FormPreview to fill height */
:deep(.form-preview) {
  display: flex;
  flex-direction: column;
  height: 100%;
}

:deep(.form-preview .card) {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

:deep(.form-preview .card-body) {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
.spin {
  display: inline-block;
  animation: spin 0.75s linear infinite;
}
</style>
